import { useEffect, useMemo, useState } from 'react';
import type { FilterGroup } from '@practium/ui';
import { TABLE_PAGE_SIZE } from '../constants/parameters.constants';

interface UseTableListParams<T> {
	items: T[];
	filterGroups?: FilterGroup[];
	getSearchText?: (item: T) => string;
	getFilterTags?: (item: T) => string[];
	pageSize?: number;
}

const prefixOf = (value: string) => value.split(':')[0];

export const useTableList = <T>({
	items,
	filterGroups = [],
	getSearchText,
	getFilterTags,
	pageSize = TABLE_PAGE_SIZE,
}: UseTableListParams<T>) => {
	const [query, setQuery] = useState('');
	const [filterValues, setFilterValues] = useState<string[]>([]);
	const [page, setPage] = useState(1);

	useEffect(() => {
		setPage(1);
	}, [query, filterValues]);

	const filtered = useMemo(() => {
		const term = query.trim().toLowerCase();
		const prefixes = [...new Set(filterValues.map(prefixOf))];
		return items.filter((item) => {
			if (term && !getSearchText?.(item).toLowerCase().includes(term)) return false;
			const tags = getFilterTags?.(item) ?? [];
			return prefixes.every((prefix) =>
				filterValues.some((value) => prefixOf(value) === prefix && tags.includes(value))
			);
		});
	}, [filterValues, getFilterTags, getSearchText, items, query]);

	const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
	const activePage = Math.min(page, totalPages);

	return {
		query,
		setQuery,
		filterValues,
		setFilterValues,
		filterGroups,
		page: activePage,
		setPage,
		totalPages,
		total: filtered.length,
		isSearchable: !!getSearchText,
		isFiltered: !!query.trim() || filterValues.length > 0,
		visible: filtered.slice((activePage - 1) * pageSize, activePage * pageSize),
	};
};

export type TableList<T> = ReturnType<typeof useTableList<T>>;
